import { Bar, BarChart, Rectangle, XAxis, type BarShapeProps } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetStat,
  WidgetTitle,
} from "@components/ui/widget";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import {
  getMCPMonitorGrowthRate,
  getMCPMonitorTimeSeries,
} from "@/api/endpoints/searches";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import { useI18n } from "@/contexts/i18nContext";
import {
  getMCPMonitorTimeSeriesGranularityFromDayDiff,
  getMCPMonitorFiltersFromSearchFilterV4,
  formatDateFromMCPMonitorTimeSeriesGranularity,
  getPeriodFromStartDateAndGranularity,
} from "./utils";
import { generatePath, useHistory } from "react-router-dom";
import { ROUTES } from "@/routes_new";
import { subDays } from "date-fns";
import { cn } from "@/lib/utils";
import { formatDateForApi } from "@/utils";
import useTimezone from "@/hooks/useActiveTimezone";

const BAR_RADIUS = 4;

function BarShape({ value, x, y, width, height, ...rest }: BarShapeProps) {
  if (value === 0) {
    return (
      <Rectangle
        {...rest}
        x={x}
        y={y}
        width={width}
        height={height}
        radius={BAR_RADIUS}
        fill="var(--color-muted)"
      />
    );
  }

  return (
    <Rectangle
      {...rest}
      x={x}
      y={y}
      width={width}
      height={height}
      radius={BAR_RADIUS}
    />
  );
}

export default function ReachOverTimeWidget({
  diffInDays,
  searchUuid,
  className,
  orgUuid,
  filters,
  ...rest
}: WidgetProps & {
  filters: SearchFilterV4WithDates;
  diffInDays: number;
  searchUuid: Uuid;
  orgUuid: Uuid;
}) {
  const { abbreviateNumber, formatCurrency, formatDate, currency } = useI18n();
  const { t } = useLingui();
  const timezone = useTimezone();

  // URL stte
  const history = useHistory();

  // Server state
  const { data: totalReachData, isLoading: isTotalReachLoading } = useQuery({
    queryKey: [searchUuid, "reach-growth-rate", filters],
    queryFn: () =>
      getMCPMonitorGrowthRate(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        compare_start_date: formatDateForApi(
          subDays(new Date(filters.start_date), diffInDays + 1)
        ),
        compare_end_date: formatDateForApi(
          subDays(new Date(filters.end_date), diffInDays + 1)
        ),
        metric: "reach",
      }),
  });

  const granularity = getMCPMonitorTimeSeriesGranularityFromDayDiff(diffInDays);

  const { data: reachTimeSeriesData, isLoading: isReachTimeSeriesLoading } =
    useQuery({
      queryKey: [searchUuid, "reach-time-series", filters],
      queryFn: () =>
        getMCPMonitorTimeSeries(searchUuid, {
          ...getMCPMonitorFiltersFromSearchFilterV4(filters),
          metric: "reach",
          granularity,
        }),
    });

  const { data: prValueTimeSeriesData, isLoading: isPrValueTimeSeriesLoading } =
    useQuery({
      queryKey: [searchUuid, "pr-value-time-series", filters],
      queryFn: () =>
        getMCPMonitorTimeSeries(searchUuid, {
          ...getMCPMonitorFiltersFromSearchFilterV4(filters),
          metric: "pr_value",
          granularity,
          currency,
        }),
    });

  // Helpers
  const isLoading =
    isTotalReachLoading ||
    isReachTimeSeriesLoading ||
    isPrValueTimeSeriesLoading;

  const noReachValues =
    reachTimeSeriesData?.ok &&
    !reachTimeSeriesData.data.points.some((point) => point.value);

  const isEmpty =
    !isLoading &&
    (!reachTimeSeriesData?.ok || !prValueTimeSeriesData?.ok || noReachValues);

  const chartConfig = {
    reach: {
      label: t`Räckvidd`,
      color: "var(--color-violet-500)",
    },
    prValue: {
      label: t`Estimerat mediavärde`,
    },
  } satisfies ChartConfig;

  return (
    <Widget className={cn("h-32 overflow-visible", className)} {...rest}>
      <WidgetHeader isLoading={isLoading}>
        <WidgetTitle>
          <Trans>Räckvidd</Trans>
        </WidgetTitle>
        {!isEmpty && totalReachData?.ok ? (
          <WidgetStat change={totalReachData.data.change_pct / 100}>
            {abbreviateNumber(totalReachData.data.value, 0)}
          </WidgetStat>
        ) : null}
      </WidgetHeader>
      <WidgetContent
        isLoading={isLoading}
        className="justify-end"
        isEmpty={isEmpty}
      >
        {reachTimeSeriesData?.ok && prValueTimeSeriesData?.ok ? (
          <ChartContainer
            config={chartConfig}
            className="h-full w-full aspect-auto"
          >
            <BarChart
              accessibilityLayer
              data={reachTimeSeriesData.data.points.map((point, i) => ({
                date: point.date,
                reach: point.value,
                prValue: prValueTimeSeriesData.data.points[i]?.value ?? 0,
              }))}
              barCategoryGap="5%"
              margin={{ bottom: 0 }}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                if (!e.activeLabel || typeof e.activeLabel !== "string") {
                  return;
                }

                const { startDate, endDate } =
                  getPeriodFromStartDateAndGranularity(
                    e.activeLabel,
                    granularity,
                    timezone
                  );

                history.replace({
                  pathname: generatePath(ROUTES.MENTION_DETAIL_ORGID_PERIOD, {
                    startDate: formatDate(startDate, "yyyy-MM-dd HH:mm:ss"),
                    endDate: formatDate(endDate, "yyyy-MM-dd HH:mm:ss"),
                    uuid: searchUuid,
                    orgUuid,
                  }),
                  search: location.search,
                });
              }}
            >
              <XAxis
                hide
                dataKey="date"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) =>
                  formatDateFromMCPMonitorTimeSeriesGranularity(
                    value,
                    granularity,
                    formatDate
                  )
                }
                interval="preserveStartEnd"
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    hideIndicator
                    labelFormatter={(value) =>
                      formatDateFromMCPMonitorTimeSeriesGranularity(
                        value as string,
                        granularity,
                        formatDate
                      )
                    }
                    formatter={(_value, _name, item, index) => {
                      if (index !== 0) return null;

                      const reach = Number(item.payload?.reach ?? 0);
                      const prValue = Number(item.payload?.prValue ?? 0);

                      return (
                        <div className="grid flex-1 gap-1.5">
                          <div className="flex items-center justify-between gap-2 leading-none">
                            <span className="text-muted-foreground">
                              {chartConfig.reach.label}
                            </span>
                            <span className="font-mono font-medium text-foreground tabular-nums">
                              {abbreviateNumber(reach, 0)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between gap-2 leading-none">
                            <span className="text-muted-foreground">
                              {chartConfig.prValue.label}
                            </span>
                            <span className="font-mono font-medium text-foreground tabular-nums">
                              {formatCurrency(prValue, currency)}
                            </span>
                          </div>
                        </div>
                      );
                    }}
                  />
                }
              />
              <Bar
                fill="var(--color-reach)"
                isAnimationActive={false}
                dataKey="reach"
                name="reach"
                minPointSize={2}
                shape={BarShape}
              />
            </BarChart>
          </ChartContainer>
        ) : null}
      </WidgetContent>
    </Widget>
  );
}
